/*
 * Blog landing live search
 *
 * Custom popover for the new-blog template's `blog-search` section.
 * Independent of the storefront's predictive-search drawer (and of any
 * third-party instant-search app injected by the theme).
 *
 * Uses Shopify's Predictive Search JSON endpoint:
 *   /search/suggest.json?q=...&resources[type]=article,page&resources[limit]=10
 *   &resources[options][fields]=title,body,tag
 *
 * Element contract (rendered in sections/blog-search.liquid):
 *   <blog-search data-predictive-url="/search/suggest">
 *     <form>...
 *       <input type="search" name="q">
 *     </form>
 *     <div data-blog-search-popover hidden>
 *       <div data-blog-search-status></div>
 *       <div data-blog-search-results></div>
 *     </div>
 *   </blog-search>
 */
class BlogSearch extends HTMLElement {
  constructor() {
    super();

    this.minChars = 2;
    this.debounceMs = 200;
    this.resourceLimit = 10;
    this.searchFields = 'title,body,tag';
    this.searchTypes = 'article,page';

    this.cache = new Map();
    this.lastQuery = '';
    this.activeAbort = null;
    this.debounceTimer = null;

    this.handleInput = this.handleInput.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"], input[name="q"]');
    this.form = this.querySelector('form');
    /*
      Property is `popoverEl`, NOT `popover`, on purpose.

      HTMLElement has a native `popover` IDL attribute (reflecting the
      `popover=""` HTML attribute used by the native Popover API) with
      a getter that returns one of the strings "auto" | "manual" | null
      and a setter that only accepts those strings. Searchanise's
      storefront script tags our `<blog-search>` custom element with
      `popover="manual"` to drive its own dropdown via the native API
      — even when the input itself is excluded via `snize-exclude-input`.
      Once that attribute is on the host, `this.popover = someElement`
      gets rejected by the native setter; subsequent reads return the
      string "manual", and `"manual".hidden = true` throws
      "Cannot create property 'hidden' on string 'manual'". Use a
      non-colliding name and the reference survives.
    */
    this.popoverEl = this.querySelector('[data-blog-search-popover]');
    this.statusEl = this.querySelector('[data-blog-search-status]');
    this.resultsEl = this.querySelector('[data-blog-search-results]');

    if (!this.input || !this.popoverEl || !this.resultsEl) return;

    this.predictiveUrl =
      this.dataset.predictiveUrl ||
      (window.routes && window.routes.predictive_search_url) ||
      '/search/suggest';

    this.input.addEventListener('input', this.handleInput);
    this.input.addEventListener('focus', this.handleFocus);
    this.addEventListener('keydown', this.handleKeyDown);
    if (this.form) this.form.addEventListener('submit', this.handleSubmit);
    document.addEventListener('click', this.handleDocumentClick);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.handleInput);
    this.input?.removeEventListener('focus', this.handleFocus);
    this.removeEventListener('keydown', this.handleKeyDown);
    this.form?.removeEventListener('submit', this.handleSubmit);
    document.removeEventListener('click', this.handleDocumentClick);
    if (this.activeAbort) this.activeAbort.abort();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  handleInput() {
    const q = this.getQuery();

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    if (q.length < this.minChars) {
      this.close();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.runSearch(q);
    }, this.debounceMs);
  }

  handleFocus() {
    const q = this.getQuery();
    if (q.length >= this.minChars && this.cache.has(q)) {
      this.render(this.cache.get(q), q);
    }
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.input?.focus();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const links = Array.from(
        this.resultsEl.querySelectorAll('a.wt-blog-search-popover__item')
      );
      if (links.length === 0) return;

      event.preventDefault();
      const currentIndex = links.indexOf(document.activeElement);
      let nextIndex;
      if (event.key === 'ArrowDown') {
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % links.length;
      } else {
        nextIndex =
          currentIndex <= 0 ? links.length - 1 : currentIndex - 1;
      }
      links[nextIndex].focus();
    }
  }

  handleSubmit(event) {
    if (this.getQuery().length === 0) {
      event.preventDefault();
    }
  }

  getQuery() {
    return (this.input?.value || '').trim();
  }

  async runSearch(q) {
    if (this.cache.has(q)) {
      this.render(this.cache.get(q), q);
      return;
    }

    if (this.activeAbort) this.activeAbort.abort();
    this.activeAbort = new AbortController();

    this.lastQuery = q;
    this.showStatus('Searching…');
    this.open();

    const params = new URLSearchParams();
    params.set('q', q);
    params.set('resources[type]', this.searchTypes);
    params.set('resources[limit]', String(this.resourceLimit));
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][fields]', this.searchFields);
    params.set(
      'resources[options][unavailable_products]',
      'last'
    );

    const url = `${this.predictiveUrl}.json?${params.toString()}`;

    try {
      const response = await fetch(url, {
        signal: this.activeAbort.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      const data = await response.json();

      if (q !== this.lastQuery) return;

      const articles =
        data?.resources?.results?.articles?.slice(0, this.resourceLimit) || [];
      const pages =
        data?.resources?.results?.pages?.slice(0, this.resourceLimit) || [];

      const payload = { articles, pages };
      this.cache.set(q, payload);
      this.render(payload, q);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.showStatus('Search is unavailable right now.');
    }
  }

  render(payload, q) {
    const { articles, pages } = payload;
    const total = (articles?.length || 0) + (pages?.length || 0);

    if (total === 0) {
      this.showStatus(`No results for "${this.escape(q)}".`);
      return;
    }

    this.statusEl.hidden = true;

    let html = '';
    if (articles.length || pages.length) {
      html += '<div class="wt-blog-search-popover__group">';
      html += `<div class="wt-blog-search-popover__heading">Articles</div>`;
      html += '<ul class="wt-blog-search-popover__list" role="listbox">';
      // Articles first
      for (const article of articles) {
        html += this.renderItem(article, 'article');
      }
      for (const page of pages) {
        html += this.renderItem(page, 'page');
      }
      html += '</ul>';
      html += '</div>';
    }

    this.resultsEl.innerHTML = html;
    this.resultsEl.hidden = false;
    this.open();
  }

  renderItem(item, type) {
    const url = item.url || '#';
    const title = this.escape(item.title || '');
    return `
      <li role="option" class="wt-blog-search-popover__cell">
        <a
          class="wt-blog-search-popover__item wt-blog-search-popover__item--${type}"
          href="${url}"
        >
          ${title}
        </a>
      </li>
    `;
  }

  showStatus(message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.hidden = false;
    this.resultsEl.innerHTML = '';
    this.resultsEl.hidden = true;
    this.open();
  }

  open() {
    if (!this.popoverEl) return;
    this.popoverEl.hidden = false;
    this.setAttribute('open', '');
  }

  close() {
    if (!this.popoverEl) return;
    this.popoverEl.hidden = true;
    this.removeAttribute('open');
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (ch) => {
      switch (ch) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
      }
      return ch;
    });
  }
}

if (!customElements.get('blog-search')) {
  customElements.define('blog-search', BlogSearch);
}
